import { useState, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { Button } from '@/components/atoms/Button';
import { Menu, MenuItem } from '@/components/atoms/Menu';
import { DateRangePicker } from '@/components/molecules/DateRangePicker';
import type { DateRangeValue } from '@/components/molecules/DateRangePicker';
import {
  DateRangeDropdownProps,
  DateRangePreset,
} from './DateRangeDropdown.props';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  startOfToday,
  endOfToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
  format,
  startOfDay,
  endOfDay,
  subWeeks,
} from 'date-fns';

/**
 * DateRangeDropdown Component
 *
 * A dropdown component for selecting date ranges with preset options (Today, This Week, This Month)
 * and a custom date range picker.
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState<DateRangeValue>({
 *   startDate: null,
 *   endDate: null,
 * });
 *
 * <DateRangeDropdown value={dateRange} onChange={setDateRange} />
 * ```
 */
export const DateRangeDropdown = ({
  value,
  onChange,
  dateFormat = 'MM/dd/yyyy',
}: DateRangeDropdownProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  // Internal state to track date range changes while dropdown is open
  const [tempDateRange, setTempDateRange] = useState<DateRangeValue>(value);
  const open = Boolean(anchorEl);

  // Calculate preset date ranges
  const todayRange = useMemo(
    () => ({
      startDate: startOfToday(),
      endDate: endOfToday(),
    }),
    []
  );

  const thisWeekRange = useMemo(
    () => ({
      startDate: startOfWeek(new Date(), { weekStartsOn: 1 }), // Monday
      endDate: endOfWeek(new Date(), { weekStartsOn: 1 }), // Sunday
    }),
    []
  );

  const lastWeekRange = useMemo(() => {
    const lastWeekStart = subWeeks(
      startOfWeek(new Date(), { weekStartsOn: 1 }),
      1
    );
    const lastWeekEnd = subWeeks(endOfWeek(new Date(), { weekStartsOn: 1 }), 1);
    return {
      startDate: lastWeekStart,
      endDate: lastWeekEnd,
    };
  }, []);

  const thisMonthRange = useMemo(
    () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
    []
  );

  // Determine current preset based on selected dates
  const currentPreset = useMemo<DateRangePreset | null>(() => {
    if (!value.startDate || !value.endDate) return null;

    const start = startOfDay(value.startDate);
    const end = endOfDay(value.endDate);

    if (
      isSameDay(start, todayRange.startDate) &&
      isSameDay(end, todayRange.endDate)
    ) {
      return 'today';
    }

    if (
      isSameDay(start, thisWeekRange.startDate) &&
      isSameDay(end, thisWeekRange.endDate)
    ) {
      return 'thisWeek';
    }

    // Check if it's last week - show as "This Week" on trigger
    if (
      isSameDay(start, lastWeekRange.startDate) &&
      isSameDay(end, lastWeekRange.endDate)
    ) {
      return 'thisWeek'; // Show as "This Week" on trigger
    }

    if (
      isSameDay(start, thisMonthRange.startDate) &&
      isSameDay(end, thisMonthRange.endDate)
    ) {
      return 'thisMonth';
    }

    return 'custom';
  }, [value, todayRange, thisWeekRange, lastWeekRange, thisMonthRange]);

  // Check if date range is valid (both dates selected and endDate >= startDate)
  const isValidDateRange = (range: DateRangeValue): boolean => {
    if (!range.startDate || !range.endDate) {
      return false;
    }
    return range.endDate >= range.startDate;
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setShowCustomPicker(false);
    // Initialize tempDateRange with current value when opening
    setTempDateRange(value);
  };

  const handleClose = (_event?: unknown, _reason?: string) => {
    // If showing custom picker, check if date range is valid before closing
    if (showCustomPicker) {
      if (!isValidDateRange(tempDateRange)) {
        // Prevent closing if date range is invalid
        return;
      }
      // Date range is valid, call onChange and close
      onChange(tempDateRange);
    }
    setAnchorEl(null);
    setShowCustomPicker(false);
    // Reset tempDateRange to current value when closing
    setTempDateRange(value);
  };

  const handlePresetClick = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setShowCustomPicker(true);
      return;
    }

    let range: DateRangeValue;
    switch (preset) {
      case 'today':
        range = todayRange;
        break;
      case 'thisWeek':
        range = thisWeekRange;
        break;
      case 'thisMonth':
        range = thisMonthRange;
        break;
      default:
        return;
    }

    onChange(range);
    handleClose();
  };

  const handleCustomDateChange = (newRange: DateRangeValue) => {
    // Only update tempDateRange, don't call onChange yet
    setTempDateRange(newRange);
  };

  // Get trigger button text
  // If last week is selected, show "This Week" on trigger
  const getTriggerText = () => {
    if (currentPreset === 'today') return 'Today';
    if (currentPreset === 'thisWeek') {
      // Check if it's actually last week
      if (
        value.startDate &&
        value.endDate &&
        isSameDay(startOfDay(value.startDate), lastWeekRange.startDate) &&
        isSameDay(endOfDay(value.endDate), lastWeekRange.endDate)
      ) {
        return 'This Week'; // Show "This Week" even if it's last week
      }
      return 'This Week';
    }
    if (currentPreset === 'thisMonth') return 'This Month';
    if (value.startDate && value.endDate) {
      // dateFormat prop is already in date-fns format
      return `${format(value.startDate, dateFormat)} - ${format(value.endDate, dateFormat)}`;
    }
    return 'Select Date Range';
  };

  return (
    <>
      <Button
        variant="text"
        onClick={handleClick}
        aria-controls={open ? 'date-range-dropdown-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        startIcon={
          <CalendarTodayIcon
            sx={{
              fontSize: 18,
              color: theme.palette.M3.sys.light.primary,
              width: 18,
              height: 18,
            }}
          />
        }
      >
        <Box
          component="span"
          sx={{
            ...theme.typography.M3.label.mediumProminent,
            color: theme.palette.M3.sys.light.primary,
          }}
        >
          {getTriggerText()}
        </Box>
      </Button>
      <Menu
        id="date-range-dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={
          showCustomPicker
            ? {
                '& .MuiPaper-root': {
                  width: 'max-content',
                  maxWidth: 'max-content',
                },
              }
            : undefined
        }
      >
        {!showCustomPicker ? (
          <>
            <MenuItem
              onClick={() => handlePresetClick('today')}
              selected={currentPreset === 'today'}
            >
              Today
            </MenuItem>
            <MenuItem
              onClick={() => handlePresetClick('thisWeek')}
              selected={currentPreset === 'thisWeek'}
            >
              This Week
            </MenuItem>
            <MenuItem
              onClick={() => handlePresetClick('thisMonth')}
              selected={currentPreset === 'thisMonth'}
            >
              This Month
            </MenuItem>
            <MenuItem
              onClick={() => handlePresetClick('custom')}
              selected={currentPreset === 'custom'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.unit(1.5),
                color: theme.palette.M3.sys.light.primary,
                '&:hover': {
                  backgroundColor:
                    theme.palette.M3.sys.light['secondary-container'],
                  color: theme.palette.M3.sys.light.primary,
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.M3.sys.light.primary,
                  },
                  '& span': {
                    color: theme.palette.M3.sys.light.primary,
                  },
                },
                '&.Mui-selected': {
                  backgroundColor:
                    theme.palette.M3.sys.light['secondary-container'],
                  color: theme.palette.M3.sys.light.primary,
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.M3.sys.light.primary,
                  },
                  '& span': {
                    color: theme.palette.M3.sys.light.primary,
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.M3.sys.light.primary,
                },
                '& span': {
                  color: theme.palette.M3.sys.light.primary,
                },
              }}
            >
              <Box component="span">Custom Date</Box>
              <CalendarTodayIcon
                sx={{
                  fontSize: 24,
                }}
              />
            </MenuItem>
          </>
        ) : (
          <DateRangePicker
            value={tempDateRange}
            onChange={handleCustomDateChange}
            dateFormat={dateFormat}
          />
        )}
      </Menu>
    </>
  );
};

export default DateRangeDropdown;
